import { apiClient, getAuthToken } from "../../api/client";
import {
  parseAirshowReviewResponse,
  type AirshowReviewRequest,
  type AirshowReviewResponse,
} from "./airshowReviewContract";
import type { AirshowReviewFeedback } from "./airshowReviewFeedback";

export interface AirshowReviewEntryIdentity {
  id: string;
  archiveMemberPath: string;
  sourceSha256: string;
}

export interface AirshowReviewFeedbackSaveReceipt {
  schema: "loom.airshow-review-feedback-save/v1";
  saved: true;
  filename: string;
  sha256: string;
  bytes: number;
}

const SAVE_RECEIPT_KEYS = ["bytes", "filename", "saved", "schema", "sha256"] as const;
const SAVE_RECEIPT_FILENAME = "openvsp-airshow-review-feedback.json";

export function parseAirshowReviewFeedbackSaveReceipt(
  value: unknown,
): AirshowReviewFeedbackSaveReceipt {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Airshow feedback save receipt must be an object");
  }
  const receipt = value as Record<string, unknown>;
  const keys = Object.keys(receipt).sort();
  if (keys.length !== SAVE_RECEIPT_KEYS.length
      || keys.some((key, index) => key !== SAVE_RECEIPT_KEYS[index])) {
    throw new Error("Airshow feedback save receipt has an unsupported field set");
  }
  if (receipt.schema !== "loom.airshow-review-feedback-save/v1" || receipt.saved !== true) {
    throw new Error("Airshow feedback save receipt has an unsupported identity");
  }
  if (receipt.filename !== SAVE_RECEIPT_FILENAME) {
    throw new Error("Airshow feedback save receipt filename is not the fixed handoff file");
  }
  if (typeof receipt.sha256 !== "string" || !/^[a-f0-9]{64}$/.test(receipt.sha256)) {
    throw new Error("Airshow feedback save receipt SHA-256 must be a bare lowercase digest");
  }
  if (!Number.isInteger(receipt.bytes) || (receipt.bytes as number) <= 0) {
    throw new Error("Airshow feedback save receipt bytes must be a positive integer");
  }
  return {
    schema: receipt.schema,
    saved: receipt.saved,
    filename: receipt.filename,
    sha256: receipt.sha256,
    bytes: receipt.bytes as number,
  };
}

export async function requestAirshowReview(input: {
  projectId: string;
  branch?: string;
  entry: AirshowReviewEntryIdentity;
  resolution?: number;
  signal?: AbortSignal;
}): Promise<AirshowReviewResponse> {
  const resolution = input.resolution ?? 8;
  if (!Number.isInteger(resolution) || resolution < 2 || resolution > 40) {
    throw new Error("Airshow review resolution must be an integer from 2 through 40");
  }
  if (!input.projectId.trim()) throw new Error("Airshow review requires a project identity");
  const request: AirshowReviewRequest = {
    schema: "loom.airshow-review-request/v1",
    entry_id: input.entry.id,
    archive_member_path: input.entry.archiveMemberPath,
    source_sha256: input.entry.sourceSha256,
    resolution,
  };
  const query = input.branch ? `?branch=${encodeURIComponent(input.branch)}` : "";
  const response = await apiClient<unknown>(
    `/thread/v1/projects/${encodeURIComponent(input.projectId)}/aero/vsp/airshow/reviews${query}`,
    { method: "POST", signal: input.signal, body: request },
  );
  return parseAirshowReviewResponse(response, {
    entryId: input.entry.id,
    archiveMemberPath: input.entry.archiveMemberPath,
    sourceSha256: input.entry.sourceSha256,
  });
}

export async function saveAirshowReviewFeedback(input: {
  projectId: string;
  feedback: AirshowReviewFeedback;
  signal?: AbortSignal;
}): Promise<AirshowReviewFeedbackSaveReceipt> {
  if (!input.projectId.trim()) throw new Error("Airshow feedback save requires a project identity");
  if (input.feedback.schema !== "loom.airshow-review-feedback/v1") {
    throw new Error("Airshow feedback save requires the revisioned feedback contract");
  }
  if (!input.feedback.note.trim()) throw new Error("Airshow feedback save requires a review note");
  if (input.feedback.note.length > 4_000) {
    throw new Error("Airshow feedback review note must be at most 4,000 characters");
  }
  const response = await apiClient<unknown>(
    `/thread/v1/projects/${encodeURIComponent(input.projectId)}/aero/vsp/airshow/review-feedback`,
    { method: "POST", signal: input.signal, body: input.feedback },
  );
  return parseAirshowReviewFeedbackSaveReceipt(response);
}

function assertArtifactUrl(modelUrl: string): void {
  const pathname = modelUrl.split(/[?#]/, 1)[0] ?? "";
  if (!pathname.startsWith("/api/") || pathname.split("/").includes("..") || pathname.includes("\\")) {
    throw new Error("Airshow artifact URL must be a same-origin /api path");
  }
}

/** Fetches an authenticated JSON artifact. The renderer validates its glTF shape. */
export async function fetchAirshowReviewArtifact(
  modelUrl: string,
  signal?: AbortSignal,
): Promise<unknown> {
  assertArtifactUrl(modelUrl);
  const token = await getAuthToken();
  const response = await fetch(modelUrl, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal,
  });
  if (!response.ok) throw new Error(`Airshow model artifact failed (${response.status})`);
  return response.json() as Promise<unknown>;
}
